// ══════════════════════════════════════════════════════════════════════════════
// SELDOR — BranchPerformanceCards
// Card grid showing branch-level metrics with health bars
// ══════════════════════════════════════════════════════════════════════════════

import { Building2 } from 'lucide-react';
import { useTheme } from '@contexts/ThemeContext';
import { useLang } from '@contexts/LangContext';
import { Card, Grid } from '@components/ui/Primitives';

export function BranchPerformanceCards({ branches = [], sales = [] }) {
  const { theme } = useTheme();
  const { t, lang } = useLang();

  if (branches.length === 0) return null;

  // Calculate per-branch metrics from sales data
  const branchMetrics = branches.map(branch => {
    const branchSales = sales.filter(s => s.branch_id === branch.id);
    const totalRev = branchSales.reduce((s, d) => s + parseFloat(d.total_revenue || 0), 0);
    const totalOrders = branchSales.reduce((s, d) => s + (d.total_orders || 0), 0);
    const totalCancellations = branchSales.reduce((s, d) => s + (d.total_cancellations || 0), 0);

    // Health score: 100 - (cancellation % * 5)
    const cancellationRate = totalOrders > 0 ? (totalCancellations / totalOrders) * 100 : 0;
    const healthScore = Math.max(0, Math.min(100, Math.round(100 - cancellationRate * 5)));

    return {
      ...branch,
      totalRev,
      totalOrders,
      totalCancellations,
      healthScore
    };
  });

  const getHealthColor = (score) => {
    if (score >= 90) return theme.success;
    if (score >= 75) return theme.warning;
    return theme.danger;
  };

  return (
    <div>
      <h4 style={{
        margin: '0 0 16px',
        fontSize: 14,
        fontWeight: 700,
        color: theme.text,
        display: 'flex',
        alignItems: 'center',
        gap: 8
      }}>
        <Building2 size={16} color={theme.primary} />
        {t.branchPerformance}
      </h4>
      <Grid min={280}>
        {branchMetrics.map(branch => {
          const healthColor = getHealthColor(branch.healthScore);
          return (
            <Card key={branch.id} style={{ padding: 22 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16 }}>
                <div style={{
                  width: 40,
                  height: 40,
                  borderRadius: 10,
                  background: theme.primaryDim,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  <Building2 size={18} color={theme.primary} />
                </div>
                <div>
                  <div style={{ fontSize: 14, fontWeight: 700, color: theme.text }}>
                    {lang === 'ar' ? branch.name_ar : branch.name_en}
                  </div>
                  <div style={{ fontSize: 11, color: theme.textTertiary }}>
                    {lang === 'ar' ? branch.address_ar : branch.address_en}
                  </div>
                </div>
              </div>
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                gap: 12,
                marginBottom: 16
              }}>
                <div style={{ textAlign: 'center', flex: 1 }}>
                  <div style={{ fontSize: 16, fontWeight: 700, color: theme.text }}>
                    {t.sar} {(branch.totalRev / 1000).toFixed(0)}K
                  </div>
                  <div style={{ fontSize: 10, color: theme.textTertiary, marginTop: 2 }}>
                    {t.totalRevenue}
                  </div>
                </div>
                <div style={{ textAlign: 'center', flex: 1 }}>
                  <div style={{ fontSize: 16, fontWeight: 700, color: theme.text }}>
                    {branch.totalOrders.toLocaleString()}
                  </div>
                  <div style={{ fontSize: 10, color: theme.textTertiary, marginTop: 2 }}>
                    {lang === 'ar' ? 'طلبات' : 'Orders'}
                  </div>
                </div>
                <div style={{ textAlign: 'center', flex: 1 }}>
                  <div style={{ fontSize: 16, fontWeight: 700, color: theme.danger }}>
                    {branch.totalCancellations}
                  </div>
                  <div style={{ fontSize: 10, color: theme.textTertiary, marginTop: 2 }}>
                    {lang === 'ar' ? 'إلغاءات' : 'Cancels'}
                  </div>
                </div>
              </div>
              <div>
                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  marginBottom: 6
                }}>
                  <span style={{ fontSize: 12, color: theme.textSecondary }}>
                    {lang === 'ar' ? 'الصحة التشغيلية' : 'Operational Health'}
                  </span>
                  <span style={{ fontSize: 13, fontWeight: 700, color: healthColor }}>
                    {branch.healthScore}%
                  </span>
                </div>
                <div style={{
                  height: 8,
                  borderRadius: 4,
                  background: theme.bgInput,
                  overflow: 'hidden'
                }}>
                  <div style={{
                    height: '100%',
                    width: `${branch.healthScore}%`,
                    background: healthColor,
                    borderRadius: 4,
                    transition: 'width 0.8s ease-out'
                  }} />
                </div>
              </div>
            </Card>
          );
        })}
      </Grid>
    </div>
  );
}
